import { memo } from "react";
import { GiGasPump } from "react-icons/gi";
import { describeArc } from "../utils/polar";
import { gaugeColor } from "../utils/gaugeColor";
import { CX, CY, ARC_START } from "./MainArc";

const R_FILL = 222;
const R_GUIDE = 233;
const GAUGE_BOT = ARC_START; // 150°
const GAUGE_TOP = 200;
const GAUGE_SPAN = GAUGE_TOP - GAUGE_BOT; // 50°
const MARKER_START = 175;
const MARKER_END = 180;

const LABEL_X = 215;
const LABEL_Y = 105;
const ICON_X = 200;
const ICON_Y = 281;

// Static arc paths computed once
const GUIDE_PATH = describeArc(CX, CY, R_GUIDE, GAUGE_BOT, GAUGE_TOP);
const MARKER_PATH = describeArc(CX, CY, R_GUIDE, MARKER_START, MARKER_END);
const TRACK_BG_PATH = describeArc(CX, CY, R_FILL, GAUGE_BOT, GAUGE_TOP);

type Props = { liters: number; maxLiters: number };

export const LitersGauge = memo(function LitersGauge({ liters, maxLiters }: Props) {
  const frac = Math.min(Math.max(liters / maxLiters, 0), 1);
  const fillTopAngle = GAUGE_BOT + frac * GAUGE_SPAN;
  const color = gaugeColor(frac);

  return (
    <>
      <defs>
        <filter id="greenGlow" x="-80%" y="-40%" width="260%" height="180%">
          <feGaussianBlur stdDeviation="5" />
        </filter>
      </defs>

      {/* Outer guide arc — 1px hairline */}
      <path
        d={GUIDE_PATH}
        fill="none"
        stroke="#D9D9D9"
        strokeWidth="1"
        strokeOpacity="0.15"
        strokeLinecap="butt"
      />
      {/* White marker — small 2px tick on the hairline */}
      <path
        d={MARKER_PATH}
        fill="none"
        stroke="#ffffff"
        strokeWidth="2"
        strokeOpacity="0.8"
        strokeLinecap="butt"
      />

      {/* Fill track background */}
      <path
        d={TRACK_BG_PATH}
        fill="none"
        stroke="#1a1c22"
        strokeWidth="7"
        strokeLinecap="butt"
      />
      {/* Green fill glow */}
      {frac > 0 && (
        <path
          d={describeArc(CX, CY, R_FILL, GAUGE_BOT, fillTopAngle)}
          fill="none"
          stroke={color}
          strokeWidth="7"
          strokeOpacity="0.4"
          strokeLinecap="butt"
          filter="url(#greenGlow)"
        />
      )}
      {/* Green active fill */}
      {frac > 0 && (
        <path
          d={describeArc(CX, CY, R_FILL, GAUGE_BOT, fillTopAngle)}
          fill="none"
          stroke={color}
          strokeWidth="7"
          strokeLinecap="butt"
        />
      )}

      {/* Liters number */}
      <text
        x={LABEL_X}
        y={LABEL_Y}
        textAnchor="end"
        dominantBaseline="hanging"
        fill="#ffffff"
        fontSize="25"
        fontWeight="600"
        className="purista-font"
      >
        {liters}
      </text>
      {/* LITERS label */}
      <text
        x={LABEL_X}
        y={LABEL_Y + 30}
        textAnchor="end"
        dominantBaseline="hanging"
        fill="#7a7d88"
        fontSize="12"
        className="purista-font uppercase"
        letterSpacing="0.22em"
      >
        LITERS
      </text>

      {/* Gas pump icon */}
      <foreignObject x={ICON_X} y={ICON_Y} width={26} height={26}>
        <div style={{ color: "#ffffff", display: "flex" }}>
          <GiGasPump size={26} />
        </div>
      </foreignObject>
    </>
  );
});
